/**
 * One job: its phases, where it is, and its log (gaps plan §8, §9).
 *
 * The stream is joinable by anyone for any job, including one this browser
 * never started and one that has already finished — so this component is
 * the same whether it is showing live work or a record from yesterday.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import type { JobRecord, JobStatus, JobStep } from '@server/jobs/store.ts'
import { api, keys } from '~/lib/api.ts'
import { useEventSource } from '~/lib/sse.ts'
import { useT } from '~/i18n/index.tsx'
import { count, duration } from '~/i18n/format.ts'
import { Badge, Button, Bar, type State } from '~/components/ui/primitives.tsx'
import { Untranslated } from '~/components/ui/raw.tsx'
import { cn } from '~/lib/cn.ts'
import type { Key } from '~/i18n/en.ts'

const STATUS_LABEL: Record<JobStatus, Key> = {
  queued: 'job.queued', running: 'job.running', done: 'job.done',
  failed: 'job.failed', cancelled: 'job.cancelled', interrupted: 'job.interrupted',
}

/**
 * Four terminal states, four appearances. `interrupted` is warn rather than
 * fail on purpose: nothing went wrong with the work, the server went away,
 * and the remedy is one button rather than an investigation.
 */
const STATUS_TONE: Record<JobStatus, State> = {
  queued: 'neutral', running: 'accent', done: 'pass',
  failed: 'fail', cancelled: 'neutral', interrupted: 'warn',
}

const TERMINAL = new Set<JobStatus>(['done', 'failed', 'cancelled', 'interrupted'])

/** Live job state, from the stream, falling back to whatever was passed in. */
export function useJobStream(initial: JobRecord) {
  const qc = useQueryClient()
  const [job, setJob] = useState(initial)

  const status = useEventSource(
    TERMINAL.has(job.status) ? null : `/api/jobs/${job.id}/events`,
    {
      state: (d) => setJob(d as JobRecord),
      step: (d) => {
        const { index, step } = d as { index: number; step: JobStep }
        setJob((j) => {
          const steps = [...j.steps]
          steps[index] = step
          return { ...j, steps, stepIndex: index }
        })
      },
      progress: (d) => {
        const p = d as { index: number; done: number; total: number | null; etaMs: number | null }
        setJob((j) => {
          const steps = [...j.steps]
          const s = steps[p.index]
          if (!s) return j
          steps[p.index] = { ...s, done: p.done, total: p.total }
          return { ...j, steps, etaMs: p.etaMs } as JobRecord & { etaMs: number | null }
        })
      },
      done: () => {
        // The corpus changed, so everything that describes it must re-read.
        void qc.invalidateQueries({ queryKey: keys.gaps(initial.symbol ?? '') })
        void qc.invalidateQueries({ queryKey: ['company'] })
        void qc.invalidateQueries({ queryKey: keys.jobs(initial.symbol ?? undefined) })
      },
    },
    { closeWhen: (event) => event === 'done' },
  )

  return { job, setJob, connection: status }
}

export function JobPanel({ job: initial, compact }: { job: JobRecord; compact?: boolean }) {
  const { t, locale } = useT()
  const { job, setJob, connection } = useJobStream(initial)
  const [showLog, setShowLog] = useState(false)

  const cancel = useMutation({
    mutationFn: () => api<{ job: JobRecord }>(`/api/jobs/${job.id}/cancel`, { method: 'POST' }),
    onSuccess: ({ job: j }) => setJob(j),
  })
  const resume = useMutation({
    mutationFn: () => api<{ job: JobRecord }>(`/api/jobs/${job.id}/resume`, { method: 'POST' }),
    onSuccess: ({ job: j }) => setJob(j),
  })

  const running = !TERMINAL.has(job.status)
  const current = job.steps[job.stepIndex]
  const etaMs = (job as JobRecord & { etaMs?: number | null }).etaMs ?? null
  const elapsed = job.startedAt ? (job.finishedAt ?? Date.now()) - job.startedAt : null

  return (
    <div
      className={cn(
        'rounded-[var(--radius-container)] border bg-[var(--bg-raised)] px-3 py-2',
        running ? 'border-[var(--accent)]/40' : 'border-[var(--border)]',
      )}
    >
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <Badge state={STATUS_TONE[job.status]}>{t(STATUS_LABEL[job.status])}</Badge>
        <span className="text-[length:var(--text-data)] text-[var(--text)]">{job.label}</span>
        <span className="tnum text-[length:var(--text-micro)] text-[var(--text-faint)]">
          {t('job.step', {
            n: count(Math.min(job.stepIndex + 1, job.steps.length), locale),
            total: count(job.steps.length, locale),
          })}
        </span>
        {elapsed !== null && (
          <span className="tnum text-[length:var(--text-micro)] text-[var(--text-faint)]">
            {t('job.elapsed', { d: duration(elapsed, locale) })}
          </span>
        )}
        {running && etaMs !== null && (
          <span className="tnum text-[length:var(--text-micro)] text-[var(--text-muted)]">
            {t('job.remaining', { d: duration(etaMs, locale) })}
          </span>
        )}
        {connection === 'retrying' && (
          <span className="text-[length:var(--text-micro)] text-[var(--warn)]">
            {t('job.reconnecting')}
          </span>
        )}
        <div className="flex-1" />
        {running && (
          <Button size="sm" variant="ghost" disabled={cancel.isPending} onClick={() => cancel.mutate()}>
            {t('job.cancel')}
          </Button>
        )}
        {(job.status === 'failed' || job.status === 'cancelled' || job.status === 'interrupted') && (
          <Button size="sm" disabled={resume.isPending} onClick={() => resume.mutate()}>
            {t('job.resume')}
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={() => setShowLog((v) => !v)}>
          {showLog ? t('job.hideLog') : t('job.showLog')}
        </Button>
      </div>

      {/* The reassurance the whole feature turns on. */}
      {running && (
        <div className="mt-0.5 text-[length:var(--text-micro)] text-[var(--text-faint)]">
          {t('job.leaveSafe')}
        </div>
      )}

      {job.status === 'interrupted' && (
        <p className="mt-1 text-[length:var(--text-data)] text-[var(--warn)]">
          {t('job.interruptedWhy')}
        </p>
      )}
      {job.error && job.status === 'failed' && (
        <p className="mt-1 max-w-[80ch] text-[length:var(--text-data)] text-[var(--fail)]">
          <Untranslated>{job.error}</Untranslated>
        </p>
      )}

      {!compact && (
        <ol className="mt-2 space-y-px">
          {job.steps.map((s, i) => (
            <StepRow key={i} step={s} index={i} current={running && i === job.stepIndex} />
          ))}
        </ol>
      )}
      {compact && current && <StepRow step={current} index={job.stepIndex} current={running} />}

      {job.outcome && <OutcomeRow outcome={job.outcome} />}

      {showLog && <LogTail jobId={job.id} live={running} />}
    </div>
  )
}

/**
 * What the run actually changed (§12.1).
 *
 * The reason this is here rather than in a metrics table: the gaps screen has
 * to hedge on whether re-extracting a failing total fixes it, because nobody
 * knows. This is how that stops being unknown — one honest before/after per
 * run, in front of the person who chose to run it.
 *
 * "Nothing changed" is a result and is printed as one. It is the answer the
 * open question is most likely to have, and hiding it would quietly bias the
 * only evidence anyone is going to collect.
 */
function OutcomeRow({ outcome }: { outcome: NonNullable<JobRecord['outcome']> }) {
  const { t, locale } = useT()
  const d = outcome.delta
  const n = (x: number) => count(Math.abs(x), locale)

  const bits: { text: string; tone: State }[] = []
  if (d.passed > 0) bits.push({ text: t('job.outcomeChecks', { n: n(d.passed) }), tone: 'pass' })
  if (d.passed < 0) bits.push({ text: t('job.outcomeChecksWorse', { n: n(d.passed) }), tone: 'fail' })
  if (d.openReview < 0) bits.push({ text: t('job.outcomeReview', { n: n(d.openReview) }), tone: 'pass' })
  if (d.openReview > 0) bits.push({ text: t('job.outcomeReviewWorse', { n: n(d.openReview) }), tone: 'warn' })
  if (d.facts !== 0) bits.push({ text: `${d.facts > 0 ? '+' : '−'}${t('job.outcomeFacts', { n: n(d.facts) })}`, tone: 'neutral' })
  if (d.factsWithConcept > 0) bits.push({ text: t('job.outcomeConcepts', { n: n(d.factsWithConcept) }), tone: 'pass' })
  if (d.extractionsOk > 0) bits.push({ text: t('job.outcomePages', { n: n(d.extractionsOk) }), tone: 'neutral' })

  return (
    <div className="mt-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 border-t border-[var(--border)] pt-1.5">
      <span className="text-[length:var(--text-micro)] text-[var(--text-faint)]">
        {t('job.outcome')}
      </span>
      {bits.length === 0 ? (
        <span className="text-[length:var(--text-data)] text-[var(--text-muted)]">
          {t('job.outcomeNone')}
        </span>
      ) : (
        bits.map((b, i) => (
          <span
            key={i}
            className={cn(
              'tnum text-[length:var(--text-data)]',
              b.tone === 'pass' ? 'text-[var(--pass)]'
                : b.tone === 'fail' ? 'text-[var(--fail)]'
                : b.tone === 'warn' ? 'text-[var(--warn)]'
                : 'text-[var(--text-muted)]',
            )}
          >
            {b.text}
          </span>
        ))
      )}
    </div>
  )
}

function StepRow({ step, index, current }: { step: JobStep; index: number; current: boolean }) {
  const { t, locale } = useT()
  const pct = step.total && step.total > 0 ? step.done / step.total : null
  return (
    <li
      className={cn(
        'flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded-[var(--radius-control)] px-1.5 py-1',
        current && 'bg-[var(--accent-soft)]',
      )}
    >
      <span className="tnum w-5 text-[length:var(--text-micro)] text-[var(--text-faint)]">
        {index + 1}
      </span>
      <span
        className={cn(
          'font-[family-name:var(--font-mono)] text-[length:var(--text-tiny)]',
          step.status === 'failed' ? 'text-[var(--fail)]'
            : step.status === 'done' ? 'text-[var(--text)]'
            : 'text-[var(--text-muted)]',
        )}
      >
        {step.phase}
      </span>
      {/* Exit 3 is a success — on a re-run it is the usual answer — but it is
          a different one from "did work", and saying so stops a job that was
          entirely no-ops reading as a job that did something. */}
      {step.status === 'nothing_to_do' && (
        <span className="text-[length:var(--text-micro)] text-[var(--text-faint)]">
          {t('job.stepNothingToDo')}
        </span>
      )}
      {(current || step.status === 'done') && step.total !== null && (
        <>
          <Bar value={pct} state={step.status === 'failed' ? 'fail' : 'accent'} />
          <span className="tnum text-[length:var(--text-micro)] text-[var(--text-muted)]">
            {count(step.done, locale)} / {count(step.total, locale)}
          </span>
        </>
      )}
      {current && step.total === null && (
        <span className="text-[length:var(--text-micro)] text-[var(--text-faint)]">
          {step.startedAt ? duration(Date.now() - step.startedAt, locale) : ''}
        </span>
      )}
      {step.error && (
        <span className="text-[length:var(--text-micro)] text-[var(--fail)]">
          <Untranslated>{step.error}</Untranslated>
        </span>
      )}
    </li>
  )
}

/**
 * The pipeline's own log, unmodified and tail-only.
 *
 * Polled rather than streamed: a phase-7 run over 22 documents is hours of
 * DEBUG output, and pushing all of it down an SSE stream is a memory leak
 * with a progress bar attached. Nobody reads more than the last screenful.
 */
function LogTail({ jobId, live }: { jobId: string; live: boolean }) {
  const { t } = useT()
  const { data } = useQuery({
    queryKey: keys.jobLog(jobId),
    queryFn: () => api<{ lines: string[]; note?: string }>(`/api/jobs/${jobId}/log?tail=120`),
    refetchInterval: live ? 3_000 : false,
  })

  if (!data) return null
  if (data.lines.length === 0)
    return (
      <div className="mt-2 text-[length:var(--text-micro)] text-[var(--text-faint)]">
        {t('job.logEmpty')}
      </div>
    )

  return (
    <pre
      dir="ltr"
      className={cn(
        'mt-2 max-h-64 overflow-auto rounded-[var(--radius-control)] border border-[var(--border)]',
        'bg-[var(--bg-sunken)] px-2 py-1.5 text-start',
        'font-[family-name:var(--font-mono)] text-[length:var(--text-micro)] text-[var(--text-muted)]',
      )}
      style={{ overscrollBehavior: 'contain' }}
    >
      {data.lines.join('\n')}
    </pre>
  )
}
